import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import moment from "moment";
import DataTable from 'src/components/data-table';
import API_ROUTES from "src/config/api";
import CurrencyFormat from "src/transformer/currency-format";
import { useSelector } from "react-redux";
import { Auth } from "aws-amplify";
import Axios from "axios";
import StatBox from "../../../dashboard/pages/main/components/statistics/stat-box";
import { convertPenniesToDecimals } from "src/utils/money";
import ProviderName from "src/transformer/provider-name";
import Skeleton from 'react-loading-skeleton'
import { CalendarIcon } from '@heroicons/react/24/outline';
import { Button, Icon } from 'src/components/Component';
import { Spinner } from 'reactstrap';

const RowFormatter = ({ item, enums, childFilterState }) => {
  const history = useHistory();

  const rowClick = (event, url) => {
    history.push(url)
  }

  return (
    <tr
      key={item.bank_reference}
      className="tb-tnx-item cursor-pointer hover:bg-gray-50"
      onClick={(event) => rowClick(event, `/batches/${item.id}`)}
    >
      <td className='border-r'>
        <span className="title"><CalendarIcon className='h-4 w-4 -mt-[3px] inline-block text-violet-800' /> {moment(item.posting_date).format('Do MMMM YYYY')}</span>
      </td>
      <td className='text-center'>
        {item.bank_reference}
      </td>
      <td className='text-center'>
        {ProviderName(item.payment_provider.name)}
      </td>
      <td className='text-center border-l border-r'>
        {CurrencyFormat(enums.currency, item.currency).format(convertPenniesToDecimals(item?.charges ? item.amount - item.charges : item.amount))}
      </td>
      <td className='text-center'>
        {item.transaction_count}
      </td>
      <td className={`text-center ${item.adjustment_count >= 1 ? 'bg-rose-100 text-rose-900 font-bold' : ''}`}>
        {item.adjustment_count}
      </td>
    </tr>
  );
}

const TableHeaders = ({ childFilterState }) => (
  <tr className="tb-tnx-head">
    <th className='text-left border-r'>
      <span>Date</span>
    </th>
    <th className='text-center'>
      <span>Bank Reference</span>
    </th>
    <th className='text-center'>
      <span>Provider</span>
    </th>
    <th className='text-center border-l border-r'>
      <span>Settlement Amount</span>
    </th>
    <th className='text-center'>
      <span>Transaction<br />Count</span>
    </th>
    <th className='text-center'>
      <span>Adjustments<br />Count</span>
    </th>
  </tr>
);

const PageOptions = ({ requestParams }) => {
  const [fetchingCSV, setFetchingCSV] = useState(false);
  const [fetchingCSVTransactions, setFetchingCSVTransactions] = useState(false);

  const setLoading = (type, status) => {
    if (type === 'csv') {
      setFetchingCSV(status);
    } else if (type === 'csv-transactions') {
      setFetchingCSVTransactions(status);
    }
  }

  const createCSV = async (type) => {
    setLoading(type, true);

    const user = await Auth.currentAuthenticatedUser();

    const response = await Axios.post(
      API_ROUTES.bank['batches:get'](),
      { ...requestParams, type },
      {
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        responseType: 'blob',
      }
    );

    const filename = type === 'csv' ? 'batches.csv' : 'transactions.csv';
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    setLoading(type, false);
  }

  return (
    <ul className="nk-block-tools g-3">
      <li>
        <Button disabled={fetchingCSV} outline className="border-violet-800 bg-white text-violet-800 text-xs mr-2 hover:bg-slate-800 hover:border-slate-800" onClick={() => createCSV('csv')}>
          {
            fetchingCSV === true ? (
              <React.Fragment>
                <Spinner size="sm" color="primary" className="mr-2"> </Spinner> Preparing CSV
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Icon name="download" />
                <span>Export Batches</span>
              </React.Fragment>
            )
          }
        </Button>

        <Button disabled={fetchingCSVTransactions} outline className="border-violet-800 bg-white text-violet-800 text-xs hover:bg-slate-800 hover:border-slate-800" onClick={() => createCSV('csv-transactions')}>
          {
            fetchingCSVTransactions === true ? (
              <React.Fragment>
                <Spinner size="sm" color="primary" className="mr-2"> </Spinner> Preparing CSV
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Icon name="download" />
                <span>Export Transactions</span>
              </React.Fragment>
            )
          }
        </Button>
      </li>
    </ul>
  );
};

const TopBlock = () => {
  const { organisation } = useSelector((state) => state);

  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState({});

  const getData = useCallback(async () => {
    if (!organisation.organisation) return;

    setLoading(true);

    const user = await Auth.currentAuthenticatedUser();

    const result = await Axios(
      API_ROUTES.bank['batches:statistics'](),
      {
        method: 'get',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        params: {
          organisation: organisation.organisation,
          'show-child-organisations': organisation?.filtering?.child_organisations_filtered === true,
        }
      }
    );

    setAccountData(result.data.data);
    setLoading(false);
  });

  useEffect(() => {
    getData();
  }, [organisation.organisation, organisation?.filtering?.child_organisations_filtered]);

  return (
    <div className='clearfix'>

      <div className="grid md:grid-cols-7 gap-4 h-full mb-4">
        <div className='col-span-2'>
          <StatBox
            title={<span>Funds Settled <strong>| Today</strong></span>}
            figure={CurrencyFormat().format(loading ? 0 : convertPenniesToDecimals(accountData?.totals['today']))}
            loading={loading}
            popoverTitle="Funds Settled"
            popoverDescription="Funds settled today to the receiving bank account from the acquirer."
            className="mb-2"
          />

          <StatBox
            title={<span>Funds Settled <strong>| Last 30 Days</strong></span>}
            figure={CurrencyFormat().format(loading ? 0 : convertPenniesToDecimals(accountData?.totals['30']))}
            loading={loading}
            popoverTitle="Funds Settled"
            popoverDescription="Funds settled over the last 30 days to the receiving bank account from the acquirer."
          />

        </div>
        <div className='col-span-2'>
          <StatBox
            title={<span>Funds Settled <strong>| Last 7 Days</strong></span>}
            figure={CurrencyFormat().format(loading ? 0 : convertPenniesToDecimals(accountData?.totals['7']))}
            loading={loading}
            popoverTitle="Funds Settled"
            popoverDescription="Funds settled over the last 7 days to the receiving bank account from the acquirer."
            className="mb-2"
          />

          <StatBox
            title={<span>Funds Settled <strong>| Last 365 Days</strong></span>}
            figure={CurrencyFormat().format(loading ? 0 : convertPenniesToDecimals(accountData?.totals['365']))}
            loading={loading}
            popoverTitle="Funds Disbursed"
            popoverDescription="Funds settled over the last 365 days to the receiving bank account from the acquirer."
          />
        </div>
        <div className="card-bordered card-stretch card col-span-3 bg-gray-50">
          <div className="border-bottom bg-gray-50 mb-0 px-3 py-2">
            <h6 className="text-md text-primary">Settlement <strong>| By Acquirer MID</strong></h6>
          </div>
          <div className="card-inner-group bg-white">
            <div className="card-inner p-0">
              <table className="table table-tranx">
                <tbody>
                  <tr className="tb-tnx-head">
                    <th className='text-left'><span>MID</span></th>
                    <th className='text-center border-r'><span>Provider</span></th>
                    <th className='text-center border-r'><span>TX Count</span></th>

                    <th className='text-center'><span>First TX Date</span></th>
                  </tr>
                  {loading === true && [0, 1].map((index) => (
                    <tr key={index} className="tb-tnx-item">
                      <td colSpan="4" className="text-center">
                        <Skeleton count={1} height="40px" />
                      </td>
                    </tr>
                  ))}
                  {accountData?.mids?.map((mid, index) => (
                    <tr className="tb-tnx-item" key={index}>
                      <td className='border-r'>
                        <span className="title">{mid.provider_mid}</span>
                      </td>
                      <td className='text-center'>
                        {ProviderName(mid.provider_name)}
                      </td>
                      <td className='text-center'>
                        {new Intl.NumberFormat('en-GB').format(mid.transaction_count)}
                      </td>
                      <td className='text-center'>
                        {moment(mid.first_transaction).format('Do MMMM YYYY')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};


const Transactions = () => {
  const endpoint = API_ROUTES.bank['batches:get']();

  return (
    <div>
      <DataTable
        title="Acquirer Batches"
        itemTitle="Batches"
        columnCount={9}
        TableHeaders={TableHeaders}
        RowFormatter={RowFormatter}
        PageOptions={PageOptions}
        TopBlock={TopBlock}
        endpoint={endpoint}
        childOrganisationsOption={true}
        noHeader={false}
      />
    </div>
  );
};

export default Transactions;
